import { useQuery } from '@tanstack/react-query'
import { ChevronRight, History } from 'lucide-react'
import { useState } from 'react'
import { driver } from '@/api/driver'
import type { DriverSession } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock, PageTitle, StatusPill } from '@/components/common'
import { Button, LinkButton } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Sheet } from '@/components/ui/overlay'
import type { Tone } from '@/lib/charging'
import { count, date, dateTime, duration, kwh, npr, time } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { ReceiptPayment } from './ReceiptPayment'

const STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: 'Charging', tone: 'brand' },
  completed: { label: 'Completed', tone: 'success' },
  stopped: { label: 'Stopped', tone: 'neutral' },
  interrupted: { label: 'Interrupted', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
}

export function SessionStatus({ status }: { status: string }) {
  const { t } = useI18n()
  const s = STATUS[status] ?? { label: status, tone: 'neutral' as Tone }
  return <StatusPill tone={s.tone}>{t(s.label)}</StatusPill>
}

/** Everything a driver wants on a receipt. */
export function Receipt({ s }: { s: DriverSession }) {
  const { t } = useI18n()
  const cb = s.cost_breakdown
  const rows: [string, string][] = [
    [t('Station'), s.station_name ?? '–'],
    [t('Charger'), `${s.charger_id} #${s.connector_id}${s.connector?.connector_type ? ` · ${s.connector.connector_type}` : ''}`],
    [t('Started'), dateTime(s.start_time)],
    [t('Ended'), s.stop_time ? dateTime(s.stop_time) : '–'],
    [t('Duration'), duration(s.start_time, s.stop_time ?? undefined)],
    [t('Energy'), kwh(s.energy_kwh)],
    ...(cb?.rate_per_kwh != null ? [[t('Rate'), `${npr(cb.rate_per_kwh)} / kWh`] as [string, string]] : []),
    ...(cb?.plan_name ? [[t('Price plan'), String(cb.plan_name)] as [string, string]] : []),
    ...(s.soc_start != null && s.soc_end != null && s.soc_end > 0 ? [[t('Battery'), `${Math.round(s.soc_start)}% → ${Math.round(s.soc_end)}%`] as [string, string]] : []),
    [t('Card'), s.id_tag ?? '–'],
  ]
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-text-2">{t('Charging cost')}</p>
          <p className="tabular text-2xl font-bold">{npr(s.cost)}</p>
          <p className="text-sm text-text-2">{t('Session #{id}', { id: s.id })}</p>
        </div>
        <SessionStatus status={s.status} />
      </div>
      <ReceiptPayment s={s} />
      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.map(([k, v]) => [<dt key={k} className="text-text-2">{k}</dt>, <dd key={`${k}v`} className="text-right">{v}</dd>])}
      </dl>
      {s.error_reason && <p className="mt-3 rounded-lg bg-warning-soft p-2.5 text-[13px] text-warning">{s.error_reason}</p>}
    </div>
  )
}

export function HistoryPage() {
  const { t } = useI18n()
  const [pages, setPages] = useState(1)
  const [open, setOpen] = useState<DriverSession | null>(null)
  const q = useQuery({ queryKey: ['history', pages], queryFn: () => driver.history({ page: 1, per_page: pages * 20 }), placeholderData: (p) => p })
  const stats = useQuery({ queryKey: ['analytics'], queryFn: driver.analytics })
  if (q.isLoading) return <LoadingBlock rows={6} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  const list = q.data?.sessions ?? []

  // Group by day for scanning.
  const groups: { day: string; items: DriverSession[] }[] = []
  for (const s of list) {
    const day = date(s.start_time)
    const g = groups.at(-1)
    if (g?.day === day) g.items.push(s)
    else groups.push({ day, items: [s] })
  }

  return (
    <div>
      <PageTitle title={t('History')} />
      {stats.data && (
        <div className="mb-5 grid grid-cols-3 gap-2">
          <Card className="p-3"><p className="text-xs text-text-2">{t('Charges')}</p><p className="tabular text-lg font-semibold">{count(stats.data.total_sessions)}</p></Card>
          <Card className="p-3"><p className="text-xs text-text-2">{t('Energy')}</p><p className="tabular text-lg font-semibold">{kwh(stats.data.total_energy_kwh)}</p></Card>
          <Card className="p-3"><p className="text-xs text-text-2">{t('Spent')}</p><p className="tabular text-lg font-semibold">{npr(stats.data.total_cost)}</p></Card>
        </div>
      )}
      {!list.length ? (
        <EmptyState icon={<History />} title={t('No charges yet')} body={t('Your charges and receipts appear here.')} />
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.day}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-2">{g.day}</p>
              <Card className="divide-y divide-border">
                {g.items.map((s) => (
                  <button key={s.id} onClick={() => setOpen(s)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{s.station_name ?? s.charger_id}</p>
                      <p className="text-[13px] text-text-2">{`${time(s.start_time)} · ${kwh(s.energy_kwh)} · ${duration(s.start_time, s.stop_time ?? undefined)}`}</p>
                    </div>
                    <div className="text-right">
                      <p className="tabular font-semibold">{npr(s.cost)}</p>
                      {s.status !== 'completed' && <SessionStatus status={s.status} />}
                    </div>
                    <ChevronRight className="size-4 text-muted" />
                  </button>
                ))}
              </Card>
            </div>
          ))}
          {list.length < (q.data?.total ?? 0) && (
            <Button variant="outline" className="w-full" loading={q.isFetching} onClick={() => setPages((p) => p + 1)}>{t('Show more')}</Button>
          )}
        </div>
      )}
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)} title={t('Receipt')} footer={open && <LinkButton to={`/receipts/${open.id}`} variant="outline">{t('Open receipt')}</LinkButton>}>
        {open && <Receipt s={open} />}
      </Sheet>
    </div>
  )
}
